import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Edit3, Trash2, Package, Calendar, Hash, Weight, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { orderManager, formatCurrency, formatDate, formatQuantity, formatWeight, authManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const DispatchManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDispatch, setShowAddDispatch] = useState(false);
  const [editingDispatch, setEditingDispatch] = useState(null);
  const currentUser = authManager.getCurrentUser();
  const isAdmin = currentUser?.role === 'Admin';

  const [dispatchForm, setDispatchForm] = useState({
    dispatchDate: new Date().toISOString().split('T')[0],
    dispatchNumber: '',
    quantity: '',
    weightInKgs: ''
  });

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = () => {
    if (id) {
      const orderData = orderManager.getOrderById(parseInt(id));
      if (orderData) {
        setOrder(orderData);
      } else {
        toast({
          title: 'Order Not Found',
          description: 'The requested order could not be found.',
          variant: 'destructive'
        });
        navigate('/orders');
      }
      setLoading(false);
    }
  };

  const resetDispatchForm = () => {
    setDispatchForm({
      dispatchDate: new Date().toISOString().split('T')[0],
      dispatchNumber: '',
      quantity: '',
      weightInKgs: ''
    });
    setEditingDispatch(null);
  };

  const handleAddDispatch = async () => {
    if (!dispatchForm.dispatchNumber.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Dispatch number is required.',
        variant: 'destructive'
      });
      return;
    }

    const quantity = parseFloat(dispatchForm.quantity);
    const weightInKgs = parseFloat(dispatchForm.weightInKgs);

    if (!quantity || quantity <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Quantity must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    if (!weightInKgs || weightInKgs <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Weight in Kgs must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    if (quantity > order.balance) {
      toast({
        title: 'Validation Error',
        description: `Dispatch quantity cannot exceed balance quantity of ${formatQuantity(order.balance, order.unit)}.`,
        variant: 'destructive'
      });
      return;
    }

    const dispatchData = {
      ...dispatchForm,
      quantity,
      weightInKgs,
      unit: order.unit,
      createdBy: currentUser?.name || 'Unknown User',
      createdAt: new Date().toISOString()
    };

    try {
      const result = orderManager.addDispatch(order.id, dispatchData);
      if (result.success) {
        toast({
          title: 'Dispatch Added',
          description: `Dispatch ${dispatchForm.dispatchNumber} added successfully. Quantity: ${formatQuantity(quantity, order.unit)}, Weight: ${formatWeight(weightInKgs)}`,
        });
        loadOrder(); // Refresh order data
        resetDispatchForm();
        setShowAddDispatch(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add dispatch. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleEditDispatch = (dispatch) => {
    setDispatchForm({
      dispatchDate: dispatch.dispatchDate,
      dispatchNumber: dispatch.dispatchNumber,
      quantity: dispatch.quantity.toString(),
      weightInKgs: dispatch.weightInKgs?.toString() || ''
    });
    setEditingDispatch(dispatch);
    setShowAddDispatch(true);
  };

  const handleUpdateDispatch = async () => {
    if (!editingDispatch) return;

    const quantity = parseFloat(dispatchForm.quantity);
    const weightInKgs = parseFloat(dispatchForm.weightInKgs);

    if (!quantity || quantity <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Quantity must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    if (!weightInKgs || weightInKgs <= 0) {
      toast({
        title: 'Validation Error',
        description: 'Weight in Kgs must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    const updatedDispatch = {
      ...editingDispatch,
      ...dispatchForm,
      quantity,
      weightInKgs,
      modifiedBy: currentUser?.name || 'Unknown User',
      modifiedAt: new Date().toISOString()
    };

    try {
      const result = orderManager.updateDispatch(order.id, editingDispatch.id, updatedDispatch);
      if (result.success) {
        toast({
          title: 'Dispatch Updated',
          description: `Dispatch ${dispatchForm.dispatchNumber} updated successfully.`,
        });
        loadOrder();
        resetDispatchForm();
        setShowAddDispatch(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update dispatch. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteDispatch = async (dispatchId) => {
    if (!isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can delete dispatches.',
        variant: 'destructive'
      });
      return;
    }

    if (window.confirm('Are you sure you want to delete this dispatch? This action cannot be undone.')) {
      try {
        const result = orderManager.deleteDispatch(order.id, dispatchId);
        if (result.success) {
          toast({
            title: 'Dispatch Deleted',
            description: 'Dispatch has been deleted successfully.',
          });
          loadOrder();
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete dispatch. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h1>
          <p className="text-gray-600 mb-6">The requested order could not be found.</p>
          <Link to="/orders">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Orders
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/orders">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Orders
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Dispatch Management
              </h1>
              <p className="text-gray-600">
                {order.brandName} • Order #{order.id}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setShowAddDispatch(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Dispatch
          </Button>
        </div>

        {/* Order Summary */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Package className="w-5 h-5 mr-2" />
              Order Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <h4 className="font-medium text-gray-900">Order Details</h4>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><strong>Brand:</strong> {order.brandName}</p>
                  <p><strong>Order Date:</strong> {formatDate(order.orderDate)}</p>
                  {order.poNumber && <p><strong>PO Number:</strong> {order.poNumber}</p>}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Quantity Status</h4>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><strong>Total Quantity:</strong> {formatQuantity(order.quantity, order.unit)}</p>
                  <p><strong>Balance:</strong> <span className="text-orange-600 font-medium">{formatQuantity(order.balance, order.unit)}</span></p>
                  <p><strong>Dispatched:</strong> {formatQuantity(order.quantity - order.balance, order.unit)}</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Pricing</h4>
                <div className="mt-2 space-y-1 text-sm text-gray-600">
                  <p><strong>Order Value:</strong> {formatCurrency(order.price)}</p>
                  <p><strong>Rate per {order.unit}:</strong> {formatCurrency(order.price / order.quantity)}</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Status</h4>
                <div className="mt-2">
                  <Badge className={order.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {order.status}
                  </Badge>
                  <p className="text-sm text-gray-600 mt-1">{order.dispatches.length} dispatch(es)</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dispatches Table */}
        <Card>
          <CardHeader>
            <CardTitle>Dispatch Records</CardTitle>
          </CardHeader>
          <CardContent>
            {order.dispatches.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No dispatches recorded yet.</p>
                <p className="text-sm">Click "Add Dispatch" to create the first dispatch record.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Dispatch Number</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Weight (Kgs)</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {order.dispatches.map((dispatch) => (
                      <TableRow key={dispatch.id}>
                        <TableCell>{formatDate(dispatch.dispatchDate)}</TableCell>
                        <TableCell className="font-medium">{dispatch.dispatchNumber}</TableCell>
                        <TableCell className="text-right">
                          {formatQuantity(dispatch.quantity, dispatch.unit)}
                        </TableCell>
                        <TableCell className="text-right">
                          {dispatch.weightInKgs ? formatWeight(dispatch.weightInKgs) : 'N/A'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end space-x-1">
                            {isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditDispatch(dispatch)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteDispatch(dispatch.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dispatch Dialog */}
        <Dialog open={showAddDispatch} onOpenChange={setShowAddDispatch}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingDispatch ? 'Edit Dispatch' : 'Add New Dispatch'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dispatchDate">Dispatch Date</Label>
                  <Input
                    id="dispatchDate"
                    type="date"
                    value={dispatchForm.dispatchDate}
                    onChange={(e) => setDispatchForm(prev => ({ ...prev, dispatchDate: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dispatchNumber">Dispatch Number</Label>
                  <Input
                    id="dispatchNumber"
                    type="text"
                    placeholder="Enter dispatch number"
                    value={dispatchForm.dispatchNumber}
                    onChange={(e) => setDispatchForm(prev => ({ ...prev, dispatchNumber: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dispatchQuantity">Quantity ({order.unit})</Label>
                  <Input
                    id="dispatchQuantity"
                    type="number"
                    step="0.01"
                    min="0"
                    max={editingDispatch ? (order.balance + editingDispatch.quantity) : order.balance}
                    placeholder="Enter quantity"
                    value={dispatchForm.quantity}
                    onChange={(e) => setDispatchForm(prev => ({ ...prev, quantity: e.target.value }))}
                  />
                  <p className="text-xs text-gray-500">
                    Available: {formatQuantity(editingDispatch ? (order.balance + editingDispatch.quantity) : order.balance, order.unit)}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dispatchWeight">Total Weight (Kgs)</Label>
                  <Input
                    id="dispatchWeight"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter weight in Kgs"
                    value={dispatchForm.weightInKgs}
                    onChange={(e) => setDispatchForm(prev => ({ ...prev, weightInKgs: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={editingDispatch ? handleUpdateDispatch : handleAddDispatch}
                  className="flex-1"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingDispatch ? 'Update Dispatch' : 'Add Dispatch'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddDispatch(false);
                    resetDispatchForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DispatchManagement;